import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  CirclePause,
  Play,
  X,
} from 'lucide-react';
import { fetchTutorialProgress, updateTutorialProgress } from '../services/api';
import { isConsentGranted } from '../consent';
import { isRtlLanguage, useSettings } from '../settings';
import { formatTranslation } from './tutorialTranslations';
import { findTarget, getTutorial, TUTORIALS } from './tutorialConfig';
import './tutorial.css';

const LOCAL_PROGRESS_KEY = 'adpulse.tutorial-progress';
const TARGET_WAIT_MS = 4200;
const SPOTLIGHT_PADDING = 9;
const VIEWPORT_MARGIN = 12;
const CARD_GAP = 18;

const TutorialContext = createContext(null);

function localKey(userId) {
  return `${LOCAL_PROGRESS_KEY}.${userId || 'visitor'}`;
}

function readLocalProgress(userId) {
  if (!isConsentGranted('functional')) return {};
  try {
    return JSON.parse(window.localStorage.getItem(localKey(userId)) || '{}');
  } catch (error) {
    return {};
  }
}

function writeLocalProgress(userId, progress) {
  if (!isConsentGranted('functional')) return;
  try {
    window.localStorage.setItem(localKey(userId), JSON.stringify(progress));
  } catch (error) {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}

function rowsToProgress(rows) {
  return (rows || []).reduce((map, row) => {
    if (row?.tutorial_id) map[row.tutorial_id] = row;
    return map;
  }, {});
}

function isVisible(element) {
  if (!element?.isConnected) return false;
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
}

function resolveTarget(target) {
  const element = findTarget(target);
  return isVisible(element) ? element : null;
}

function getFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => isVisible(element) && element.getAttribute('aria-hidden') !== 'true');
}

function reducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function tutorialStatusLabel(t, status) {
  const key = {
    completed: 'complete',
    paused: 'paused',
    skipped: 'skipped',
    dismissed: 'dismissed',
    active: 'active',
    new: 'new',
  }[status || 'new'] || 'new';
  return t(`tutorial.center.${key}`);
}

export function TutorialProvider({ user, currentView, onNavigate, children }) {
  const { settings, t } = useSettings();
  const [progress, setProgress] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null);
  const progressRef = useRef(progress);
  const activeRef = useRef(active);
  const autoLaunchedRef = useRef(new Set());
  const ownerId = user?.id || '';

  useEffect(() => { progressRef.current = progress; }, [progress]);
  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    let cancelled = false;
    setActive(null);
    setLoaded(false);
    autoLaunchedRef.current = new Set();
    const local = readLocalProgress(ownerId);

    if (!ownerId) {
      setProgress(local);
      setLoaded(true);
      return () => { cancelled = true; };
    }

    fetchTutorialProgress()
      .then(({ tutorials }) => {
        if (cancelled) return;
        const remote = rowsToProgress(tutorials);
        const merged = { ...local, ...remote };
        setProgress(merged);
        writeLocalProgress(ownerId, merged);
      })
      .catch(() => {
        if (!cancelled) setProgress(local);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => { cancelled = true; };
  }, [ownerId]);

  const persist = useCallback((tutorial, status, stepIndex) => {
    if (!tutorial) return Promise.resolve();
    const record = {
      tutorial_id: tutorial.id,
      tutorial_version: tutorial.version,
      status,
      step_index: Math.max(0, stepIndex || 0),
      completed_at: status === 'completed' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    };
    const next = { ...progressRef.current, [tutorial.id]: record };
    progressRef.current = next;
    setProgress(next);
    writeLocalProgress(ownerId, next);
    if (!ownerId) return Promise.resolve(record);
    return updateTutorialProgress(tutorial.id, record)
      .then(({ tutorial: remote }) => {
        if (!remote) return record;
        setProgress((current) => {
          const merged = { ...current, [tutorial.id]: remote };
          progressRef.current = merged;
          writeLocalProgress(ownerId, merged);
          return merged;
        });
        return remote;
      })
      .catch(() => record);
  }, [ownerId]);

  const openTutorial = useCallback((tutorial, stepIndex = 0) => {
    if (!tutorial?.steps?.length) return;
    const safeIndex = Math.min(Math.max(0, stepIndex), tutorial.steps.length - 1);
    setActive({ tutorialId: tutorial.id, stepIndex: safeIndex });
    persist(tutorial, 'active', safeIndex);
  }, [persist]);

  const startTutorial = useCallback((tutorialId, options = {}) => {
    const tutorial = getTutorial(tutorialId);
    if (!tutorial) return;
    const record = progressRef.current[tutorial.id];
    const canResume = record?.tutorial_version === tutorial.version
      && ['active', 'paused'].includes(record.status)
      && !options.restart;
    const stepIndex = canResume ? record.step_index : 0;
    const activate = () => openTutorial(tutorial, stepIndex);
    if (tutorial.view !== '*' && tutorial.view !== currentView) {
      onNavigate?.(tutorial.view);
      window.setTimeout(activate, reducedMotion() ? 0 : 280);
    } else {
      activate();
    }
  }, [currentView, onNavigate, openTutorial]);

  const restartTutorial = useCallback((tutorialId) => {
    startTutorial(tutorialId, { restart: true });
  }, [startTutorial]);

  const currentTutorial = active ? getTutorial(active.tutorialId) : null;
  const currentStep = currentTutorial?.steps?.[active?.stepIndex] || null;

  const closeWithStatus = useCallback((status) => {
    const snapshot = activeRef.current;
    const tutorial = getTutorial(snapshot?.tutorialId);
    if (tutorial && snapshot) persist(tutorial, status, snapshot.stepIndex);
    setActive(null);
  }, [persist]);

  const pause = useCallback(() => closeWithStatus('paused'), [closeWithStatus]);
  const skip = useCallback(() => closeWithStatus('skipped'), [closeWithStatus]);
  const dismiss = useCallback(() => closeWithStatus('dismissed'), [closeWithStatus]);

  const finish = useCallback(() => {
    const snapshot = activeRef.current;
    const tutorial = getTutorial(snapshot?.tutorialId);
    if (tutorial) persist(tutorial, 'completed', Math.max(0, tutorial.steps.length - 1));
    setActive(null);
  }, [persist]);

  const goToStep = useCallback((nextIndex) => {
    const snapshot = activeRef.current;
    const tutorial = getTutorial(snapshot?.tutorialId);
    if (!tutorial) return;
    if (nextIndex >= tutorial.steps.length) {
      finish();
      return;
    }
    const safeIndex = Math.max(0, nextIndex);
    setActive({ tutorialId: tutorial.id, stepIndex: safeIndex });
    persist(tutorial, 'active', safeIndex);
  }, [finish, persist]);

  const next = useCallback(() => goToStep((activeRef.current?.stepIndex || 0) + 1), [goToStep]);
  const back = useCallback(() => goToStep(Math.max(0, (activeRef.current?.stepIndex || 0) - 1)), [goToStep]);

  const handleUnavailable = useCallback(() => {
    const snapshot = activeRef.current;
    const tutorial = getTutorial(snapshot?.tutorialId);
    if (!tutorial || !snapshot) return;
    for (let index = snapshot.stepIndex + 1; index < tutorial.steps.length; index += 1) {
      if (resolveTarget(tutorial.steps[index].target)) {
        goToStep(index);
        return;
      }
    }
    if (tutorial.steps[snapshot.stepIndex]?.optional) {
      finish();
      return;
    }
    closeWithStatus('paused');
  }, [closeWithStatus, finish, goToStep]);

  useEffect(() => {
    if (!active || !currentTutorial || currentTutorial.view === '*' || currentTutorial.view === currentView) return;
    pause();
  }, [active, currentTutorial, currentView, pause]);

  useEffect(() => {
    if (!loaded || !ownerId || active) return undefined;
    let timer = null;
    let observer = null;

    const findCandidate = () => TUTORIALS.find((tutorial) => {
      if (tutorial.manualOnly || tutorial.view !== currentView) return false;
      if (autoLaunchedRef.current.has(`${ownerId}:${tutorial.id}`)) return false;
      const record = progressRef.current[tutorial.id];
      if (record?.tutorial_version === tutorial.version
          && ['completed', 'skipped', 'dismissed', 'paused'].includes(record.status)) return false;
      return !tutorial.requiredTarget || Boolean(resolveTarget(tutorial.requiredTarget));
    });

    const attempt = () => {
      if (document.querySelector('[aria-modal="true"]')) return;
      const tutorial = findCandidate();
      if (!tutorial || !resolveTarget(tutorial.steps[0]?.target)) return;
      autoLaunchedRef.current.add(`${ownerId}:${tutorial.id}`);
      openTutorial(tutorial, progressRef.current[tutorial.id]?.step_index || 0);
      observer?.disconnect();
    };

    timer = window.setTimeout(attempt, reducedMotion() ? 100 : 850);
    observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(attempt, 180);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [active, currentView, loaded, openTutorial, ownerId]);

  const pausedTutorial = useMemo(() => TUTORIALS.find((tutorial) => {
    const record = progress[tutorial.id];
    return record?.tutorial_version === tutorial.version && record.status === 'paused';
  }), [progress]);

  const contextValue = useMemo(() => ({
    tutorials: TUTORIALS,
    progress,
    loaded,
    active,
    currentTutorial,
    currentStep,
    startTutorial,
    restartTutorial,
    pause,
    skip,
    dismiss,
    finish,
    next,
    back,
    statusLabel: (status) => tutorialStatusLabel(t, status),
  }), [
    active, back, currentStep, currentTutorial, dismiss, finish, loaded, next,
    pause, progress, restartTutorial, skip, startTutorial, t,
  ]);

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}
      {active && currentTutorial && currentStep && (
        <TutorialOverlay
          tutorial={currentTutorial}
          step={currentStep}
          stepIndex={active.stepIndex}
          onBack={back}
          onNext={next}
          onFinish={finish}
          onPause={pause}
          onSkip={skip}
          onDismiss={dismiss}
          onUnavailable={handleUnavailable}
          rtl={isRtlLanguage(settings.language)}
          t={t}
        />
      )}
      {!active && pausedTutorial && (
        <button
          type="button"
          className="tutorial-resume-pill"
          onClick={() => startTutorial(pausedTutorial.id)}
        >
          <Play size={15} aria-hidden="true" />
          <span>{t('tutorial.controls.resume')}</span>
          <strong>{t(pausedTutorial.titleKey)}</strong>
        </button>
      )}
    </TutorialContext.Provider>
  );
}

function TutorialOverlay({
  tutorial,
  step,
  stepIndex,
  onBack,
  onNext,
  onFinish,
  onPause,
  onSkip,
  onDismiss,
  onUnavailable,
  rtl,
  t,
}) {
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const [cardPosition, setCardPosition] = useState(null);
  const cardRef = useRef(null);
  const previousFocusRef = useRef(null);
  const frameRef = useRef(0);
  const titleId = `tutorial-title-${tutorial.id.replaceAll('.', '-')}`;
  const bodyId = `tutorial-body-${tutorial.id.replaceAll('.', '-')}`;
  const lastStep = stepIndex === tutorial.steps.length - 1;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!previousFocusRef.current) previousFocusRef.current = previous;
    return () => {
      const restore = previousFocusRef.current;
      if (restore?.isConnected) window.requestAnimationFrame(() => restore.focus({ preventScroll: true }));
    };
  }, []);

  useEffect(() => {
    let observer = null;
    let timeout = null;
    let settled = false;
    setTargetElement(null);
    setTargetRect(null);
    setCardPosition(null);

    const locate = () => {
      const element = resolveTarget(step.target);
      if (!element) return false;
      settled = true;
      setTargetElement(element);
      observer?.disconnect();
      window.clearTimeout(timeout);
      return true;
    };

    if (!locate()) {
      observer = new MutationObserver(locate);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
      timeout = window.setTimeout(() => {
        if (!settled) onUnavailable();
      }, step.optional ? 700 : TARGET_WAIT_MS);
    }

    return () => {
      observer?.disconnect();
      window.clearTimeout(timeout);
    };
  }, [onUnavailable, step]);

  useEffect(() => {
    if (!targetElement) return undefined;
    let resizeObserver = null;
    let descendantObserver = null;
    const update = () => {
      frameRef.current = 0;
      if (!isVisible(targetElement)) {
        setTargetRect(null);
        return;
      }
      const raw = targetElement.getBoundingClientRect();
      const bounds = { left: raw.left, right: raw.right, top: raw.top, bottom: raw.bottom };
      if (step.allowInteraction) {
        targetElement.querySelectorAll('*').forEach((descendant) => {
          if (!isVisible(descendant)) return;
          const rect = descendant.getBoundingClientRect();
          bounds.left = Math.min(bounds.left, rect.left);
          bounds.right = Math.max(bounds.right, rect.right);
          bounds.top = Math.min(bounds.top, rect.top);
          bounds.bottom = Math.max(bounds.bottom, rect.bottom);
        });
      }
      if (step.maxSpotlightWidth && bounds.right - bounds.left > step.maxSpotlightWidth) {
        const center = (bounds.left + bounds.right) / 2;
        bounds.left = center - step.maxSpotlightWidth / 2;
        bounds.right = center + step.maxSpotlightWidth / 2;
      }
      if (step.maxSpotlightHeight && bounds.bottom - bounds.top > step.maxSpotlightHeight) {
        const center = (bounds.top + bounds.bottom) / 2;
        bounds.top = center - step.maxSpotlightHeight / 2;
        bounds.bottom = center + step.maxSpotlightHeight / 2;
      }
      const left = Math.max(VIEWPORT_MARGIN, bounds.left - SPOTLIGHT_PADDING);
      const right = Math.min(window.innerWidth - VIEWPORT_MARGIN, bounds.right + SPOTLIGHT_PADDING);
      const top = Math.max(VIEWPORT_MARGIN, bounds.top - SPOTLIGHT_PADDING);
      const bottom = Math.min(window.innerHeight - VIEWPORT_MARGIN, bounds.bottom + SPOTLIGHT_PADDING);
      setTargetRect({ left, right, top, bottom, width: Math.max(1, right - left), height: Math.max(1, bottom - top) });
    };
    const schedule = () => {
      if (!frameRef.current) frameRef.current = window.requestAnimationFrame(update);
    };
    const raw = targetElement.getBoundingClientRect();
    if (raw.top < 88 || raw.bottom > window.innerHeight - 32) {
      targetElement.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
    targetElement.setAttribute('data-tutorial-active', 'true');
    update();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(targetElement);
    }
    if (step.allowInteraction) {
      descendantObserver = new MutationObserver(schedule);
      descendantObserver.observe(targetElement, { childList: true, subtree: true, attributes: true });
    }
    const settleTimer = window.setTimeout(update, reducedMotion() ? 0 : 360);
    return () => {
      window.clearTimeout(settleTimer);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
      descendantObserver?.disconnect();
      targetElement.removeAttribute('data-tutorial-active');
    };
  }, [step.allowInteraction, step.maxSpotlightHeight, step.maxSpotlightWidth, targetElement]);

  useLayoutEffect(() => {
    if (!targetRect || !cardRef.current) return;
    const card = cardRef.current.getBoundingClientRect();
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const horizontalPreferred = rtl && step.placement === 'left' ? 'right'
      : rtl && step.placement === 'right' ? 'left'
        : step.placement;
    const directions = [horizontalPreferred, 'bottom', 'top', rtl ? 'left' : 'right', rtl ? 'right' : 'left']
      .filter((value, index, list) => value && list.indexOf(value) === index);
    const fits = {
      bottom: viewport.height - targetRect.bottom >= card.height + CARD_GAP,
      top: targetRect.top >= card.height + CARD_GAP,
      right: viewport.width - targetRect.right >= card.width + CARD_GAP,
      left: targetRect.left >= card.width + CARD_GAP,
    };
    const available = {
      bottom: viewport.height - targetRect.bottom,
      top: targetRect.top,
      right: viewport.width - targetRect.right,
      left: targetRect.left,
    };
    const placement = directions.find((direction) => fits[direction])
      || directions.sort((a, b) => available[b] - available[a])[0]
      || 'bottom';
    const constrainedMaxHeight = !fits[placement] && ['top', 'bottom'].includes(placement)
      ? Math.max(210, available[placement] - CARD_GAP - VIEWPORT_MARGIN)
      : null;
    let left = targetRect.left + (targetRect.width - card.width) / 2;
    let top = targetRect.bottom + CARD_GAP;
    if (placement === 'top') top = targetRect.top - card.height - CARD_GAP;
    if (placement === 'right') {
      left = targetRect.right + CARD_GAP;
      top = targetRect.top + (targetRect.height - card.height) / 2;
    }
    if (placement === 'left') {
      left = targetRect.left - card.width - CARD_GAP;
      top = targetRect.top + (targetRect.height - card.height) / 2;
    }
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewport.width - card.width - VIEWPORT_MARGIN));
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewport.height - card.height - VIEWPORT_MARGIN));
    const pointerOffset = ['top', 'bottom'].includes(placement)
      ? Math.max(24, Math.min(targetRect.left + targetRect.width / 2 - left, card.width - 24))
      : Math.max(24, Math.min(targetRect.top + targetRect.height / 2 - top, card.height - 24));
    setCardPosition({ left, top, placement, maxHeight: constrainedMaxHeight, pointerOffset });
  }, [rtl, step.placement, targetRect]);

  useEffect(() => {
    if (!targetRect || !cardPosition) return;
    const id = window.requestAnimationFrame(() => cardRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(id);
  }, [cardPosition, stepIndex, targetRect]);

  useEffect(() => {
    if (!targetElement) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onPause();
        return;
      }
      if (event.key === 'ArrowRight' && event.altKey) {
        event.preventDefault();
        rtl ? onBack() : (lastStep ? onFinish() : onNext());
        return;
      }
      if (event.key === 'ArrowLeft' && event.altKey) {
        event.preventDefault();
        rtl ? (lastStep ? onFinish() : onNext()) : onBack();
        return;
      }
      if (event.key !== 'Tab') return;
      const targetFocusable = step.allowInteraction ? getFocusable(targetElement) : [];
      const cardFocusable = getFocusable(cardRef.current);
      const focusable = [...targetFocusable, ...cardFocusable];
      if (!focusable.length) {
        event.preventDefault();
        cardRef.current?.focus();
        return;
      }
      const current = focusable.indexOf(document.activeElement);
      if (event.shiftKey && (current <= 0 || document.activeElement === cardRef.current)) {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (!event.shiftKey && current === focusable.length - 1) {
        event.preventDefault();
        focusable[0].focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [lastStep, onBack, onFinish, onNext, onPause, rtl, step.allowInteraction, targetElement]);

  if (!targetRect) return null;

  const block = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const progressText = formatTranslation(t('tutorial.controls.progress'), {
    current: stepIndex + 1,
    total: tutorial.steps.length,
  });
  const BackIcon = rtl ? ChevronRight : ChevronLeft;
  const NextIcon = rtl ? ArrowLeft : ArrowRight;

  return createPortal(
    <div className="tutorial-layer" dir={rtl ? 'rtl' : 'ltr'} data-testid="tutorial-layer" data-no-translate>
      <div className="tutorial-shade tutorial-shade-top" style={{ height: targetRect.top }} onPointerDown={block} onClick={block} />
      <div className="tutorial-shade tutorial-shade-bottom" style={{ top: targetRect.bottom }} onPointerDown={block} onClick={block} />
      <div className="tutorial-shade tutorial-shade-left" style={{ top: targetRect.top, width: targetRect.left, height: targetRect.height }} onPointerDown={block} onClick={block} />
      <div className="tutorial-shade tutorial-shade-right" style={{ top: targetRect.top, left: targetRect.right, height: targetRect.height }} onPointerDown={block} onClick={block} />
      {!step.allowInteraction && (
        <div
          className="tutorial-target-blocker"
          style={{ top: targetRect.top, left: targetRect.left, width: targetRect.width, height: targetRect.height }}
          onPointerDown={block}
          onClick={block}
          aria-hidden="true"
        />
      )}
      <div
        className="tutorial-spotlight"
        style={{ top: targetRect.top, left: targetRect.left, width: targetRect.width, height: targetRect.height }}
        aria-hidden="true"
      />
      <section
        ref={cardRef}
        className={`tutorial-card ${cardPosition ? `tutorial-card--${cardPosition.placement}` : 'tutorial-card--measuring'}`}
        style={cardPosition ? {
          left: cardPosition.left,
          top: cardPosition.top,
          '--tutorial-pointer-offset': `${cardPosition.pointerOffset}px`,
          ...(cardPosition.maxHeight ? { maxHeight: cardPosition.maxHeight } : {}),
        } : { left: VIEWPORT_MARGIN, top: VIEWPORT_MARGIN }}
        role="dialog"
        aria-modal={step.allowInteraction ? 'false' : 'true'}
        aria-roledescription={t('tutorial.controls.cardLabel')}
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex="-1"
      >
        <span className="tutorial-pointer" aria-hidden="true" />
        <button type="button" className="tutorial-close" onClick={onDismiss} aria-label={t('tutorial.controls.closeLabel')}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="tutorial-meta">
          <span>{t(tutorial.titleKey)}</span>
          <strong aria-live="polite">{progressText}</strong>
        </div>
        <div className="tutorial-progress" aria-hidden="true">
          <i style={{ width: `${((stepIndex + 1) / tutorial.steps.length) * 100}%` }} />
        </div>
        <h2 id={titleId}>{t(step.titleKey)}</h2>
        <p id={bodyId}>{t(step.bodyKey)}</p>
        <div className="tutorial-secondary-actions">
          <button type="button" onClick={onSkip}>{t('tutorial.controls.skip')}</button>
          <button type="button" onClick={onPause}><CirclePause size={14} aria-hidden="true" /> {t('tutorial.controls.pause')}</button>
        </div>
        <div className="tutorial-primary-actions">
          <button type="button" className="tutorial-back" onClick={onBack} disabled={stepIndex === 0}>
            <BackIcon size={16} aria-hidden="true" /> {t('tutorial.controls.back')}
          </button>
          <button type="button" className="tutorial-next" onClick={lastStep ? onFinish : onNext}>
            {lastStep ? <Check size={16} aria-hidden="true" /> : null}
            {lastStep ? t('tutorial.controls.finish') : t('tutorial.controls.next')}
            {!lastStep ? <NextIcon size={16} aria-hidden="true" /> : null}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

export function useTutorials() {
  const value = useContext(TutorialContext);
  if (!value) throw new Error('useTutorials must be used inside TutorialProvider.');
  return value;
}
